// Updated App.jsx - Fixed routing with Always-Active Homepage and PWA Install Prompt
import React, { useEffect, useState, useContext, createContext } from 'react';
import { motion } from 'framer-motion';
import { PrivacyAuthProvider, usePrivacyAuth } from './context/PrivacyAuthContext';
import { AppProvider, useApp } from './context/AppContext';
import { GuideProvider, useGuide } from './context/GuideContext';
import Navigation from './components/shared/Navigation';
import Footer from './components/shared/Footer';
import StandaloneSignup from './components/auth/StandaloneSignup';
import { FloatingGuideButton, NanaWisdomPopup, GuideSelection } from './components/guide';

// Local SkipLink component for accessibility
const SkipLink = ({ href = '#main-content' }) => (
  <a
    href={href}
    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-white focus:rounded-lg"
  >
    Skip to main content
  </a>
);
// Import critical pages directly (not lazy) to avoid loading issues
import FriendshipVibeHomePage from './pages/FriendshipVibeHomePage'; // Friendship-specific homepage
import FriendshipModulesPage from './pages/FriendshipModulesPage'; // Import directly - critical page

// Lazy load less critical pages for better performance
const LazyFriendshipVibeDashboard = React.lazy(() => import('./pages/FriendshipVibeDashboard'));
// Assessment components
const LazyVibeAssessmentIntro = React.lazy(() => import('./components/assessment/VibeAssessmentIntro'));
const LazyVibeAssessment = React.lazy(() => import('./components/assessment/VibeAssessment'));
const LazyVibeAssessmentResultsView = React.lazy(() => import('./components/assessment/VibeAssessmentResultsView'));
const LazyFriendshipModulesIntro = React.lazy(() => import('./components/friendship/FriendshipModulesIntro'));
const LazyVibeToolsPage = React.lazy(() => import('./pages/VibeToolsPage'));
const LazyVibeResourcesPage = React.lazy(() => import('./pages/VibeResourcesPage'));
const LazyVibeDataPrivacyPage = React.lazy(() => import('./pages/VibeDataPrivacyPage'));
const LazyPrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const LazyFriendCirclesLanding = React.lazy(() => import('./pages/FriendCirclesLanding'));
const LazyFriendCirclesWelcome = React.lazy(() => import('./pages/FriendCirclesWelcome'));
const LazyCircleDashboard = React.lazy(() => import('./pages/CircleDashboard'));
const LazyMyCirclesPage = React.lazy(() => import('./pages/MyCirclesPage'));
const LazyCoachingPage = React.lazy(() => import('./pages/CoachingPage'));
const LazyCoachingPrivacyPage = React.lazy(() => import('./pages/CoachingPrivacyPage'));
import CircleSetup from './components/circles/CircleSetup';
import FriendCirclesRouter from './components/circles/FriendCirclesRouter';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/errorBoundary/ErrorBoundary';
import errorTrackingService from './services/errorTracking/ErrorTrackingService';
import VibeLogin from './components/auth/VibeLogin';

// Loading fallback component for lazy loading
const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
    <div className="text-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full mx-auto mb-4"
      />
      <div className="text-white text-lg font-semibold">Friendships Vibe</div>
      <div className="text-purple-300 text-sm mt-2">Loading your social wellness tools...</div>
    </div>
  </div>
);

const AppContent = () => {
  const { user, isLoading } = usePrivacyAuth();

  // Pull any toolkit work saved on another device down into this one.
  //
  // Deliberately fire-and-forget and deliberately NOT awaited: the hub is
  // sync, not storage, and the app is local-first. If this never resolves -
  // offline, hub down - the tools still open instantly on whatever is in this
  // browser. The idle callback keeps it off the first paint entirely.
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      import('./services/ToolSyncService')
        .then((m) => m.hydrateFromHub())
        .catch(() => { /* local-first: a failed sync is not a failed app */ });
    };
    const id = typeof window !== 'undefined' && window.requestIdleCallback
      ? window.requestIdleCallback(run, { timeout: 4000 })
      : setTimeout(run, 2000);
    return () => {
      cancelled = true;
      if (typeof window !== 'undefined' && window.cancelIdleCallback) {
        try { window.cancelIdleCallback(id); } catch { /* setTimeout id */ }
      }
    };
  }, []);
  const { currentView, completeAssessment, setCurrentView, scrollToTop } = useApp();

  // DEBUGGING: Log whenever user state changes
  useEffect(() => {
    console.log('👤 App.jsx: User state changed:', user);
    console.log('👤 Loading state:', isLoading);

    // Also log localStorage state for comparison
    const cachedUser = localStorage.getItem('vibe_user_identity');
    const authSuccess = localStorage.getItem('vibe_auth_success');
    const userId = localStorage.getItem('vibe_user_id');
    console.log('👤 localStorage state:');
    console.log('  - vibe_user_identity:', cachedUser);
    console.log('  - vibe_auth_success:', authSuccess);
    console.log('  - vibe_user_id:', userId);
  }, [user, isLoading]);

  // Scroll to top whenever the view changes
  useEffect(() => {
    scrollToTop('smooth');
  }, [currentView, scrollToTop]);

  // Handle legacy 'progress' redirect - must use useEffect to avoid setState during render
  useEffect(() => {
    if (currentView === 'progress') {
      setCurrentView('dashboard');
    }
  }, [currentView, setCurrentView]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.view) {
        setCurrentView(event.state.view);
      } else if (event.state && event.state.previousView) {
        setCurrentView(event.state.previousView);
      } else {
        // Default to modules view if no state
        setCurrentView('modules');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [setCurrentView]);

  // Views that don't require authentication (guest-friendly)
  const _guestAllowedViews = [
    'home', 
    'dashboard', // Now guest-friendly with limited features
    'vibe-assessment-intro',
    'vibe-assessment', 
    'resources',
    'data-privacy',
    'privacy-policy',
  ];

  // Views that require authentication - now only dashboard and data-privacy require auth
  const authRequiredViews = [
    'progress', // Legacy redirect to dashboard
    'life-design-hub', // New view placeholder
  ];

  const requiresAuth = authRequiredViews.includes(currentView);

  // Handle assessment completion and post-signup flow
  const handleAssessmentComplete = async (results) => {
    console.log('🎯 App.jsx: Assessment completed with results:', results);
    console.log('🎯 App.jsx: Results type:', typeof results);
    console.log('🎯 App.jsx: Results keys:', results ? Object.keys(results) : 'null/undefined');
    console.log('🎯 App.jsx: About to call completeAssessment...');

    await completeAssessment(results);

    console.log('🎯 App.jsx: completeAssessment call finished');

    // Add a small delay to ensure the data is saved before navigating
    setTimeout(() => {
      console.log('🎯 App.jsx: Navigating to dashboard after assessment');
      setCurrentView('dashboard');
    }, 500);
  };

  // Handle auth-required navigation
  const handleAuthRequiredNavigation = (_view) => {
    if (!user && requiresAuth) {
      // Redirect to pure HTML signup page
      window.location.href = '/signup.html';
      return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">Redirecting to signup...</div>
      </div>;
    }
    return null;
  };

  // Loading screen with Vibe styling - with timeout failsafe
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);

  React.useEffect(() => {
    if (isLoading) {
      console.log('⏰ Loading screen shown, setting 10s timeout');
      const timer = setTimeout(() => {
        console.error('❌ Loading timeout reached! Force continuing...');
        setLoadingTimeout(true);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  if (isLoading && !loadingTimeout) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">🤝</span>
            </div>
          </motion.div>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"
          />
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Friendships Vibe</h2>
          <p className="text-purple-200 text-sm sm:text-base">Preparing your social wellness journey...</p>
        </div>
      </div>
    );
  }

  // If loading timed out, force render anyway with error boundary
  if (loadingTimeout) {
    console.warn('⚠️ Loading timed out, forcing render with current state');
  }

  // Check if trying to access auth-required content as guest
  const authCheck = handleAuthRequiredNavigation(currentView);
  if (authCheck) return authCheck;
  
  
  const renderCurrentView = () => {
    console.log('🎯 App.jsx renderCurrentView called with:', currentView);
    console.log('🎯 User:', user);
    console.log('🎯 isLoading:', isLoading);

    // Handle all views with proper routing logic
    switch (currentView) {
      case 'dashboard':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyFriendshipVibeDashboard />
          </React.Suspense>
        );
        
      case 'vibe-assessment-intro':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessmentIntro />
          </React.Suspense>
        );

      case 'vibe-assessment':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessment onComplete={handleAssessmentComplete}
              onResults={completeAssessment} />
          </React.Suspense>
        );

      case 'vibe-assessment-results':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeAssessmentResultsView />
          </React.Suspense>
        );
        
      case 'modules-intro':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyFriendshipModulesIntro />
          </React.Suspense>
        );

      case 'modules':
        return <FriendshipModulesPage />;

      case 'progress':
        // Legacy redirect handled in useEffect above - just show dashboard
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyFriendshipVibeDashboard />
          </React.Suspense>
        );
        
      case 'tools':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeToolsPage />
          </React.Suspense>
        );

      case 'friend-circles':
        // Smart router: checks if user has circles and routes accordingly
        // - Not logged in → Landing page
        // - Has circles → Circle Dashboard
        // - No circles → Welcome page
        return <FriendCirclesRouter />;

      case 'friend-circles-welcome':
        // Welcome/onboarding page for authenticated users
        console.log('🔵 App.jsx: Rendering friend-circles-welcome');
        console.log('🔵 User state:', user);

        if (!user) {
          console.log('⚠️ No user found, redirecting to signup');
          window.location.href = '/signup.html?redirect=friend-circles-welcome';
          return null;
        }

        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyFriendCirclesWelcome />
          </React.Suspense>
        );

      case 'friend-circles-setup':
        // Actual setup - requires authentication
        console.log('🔵 App.jsx: Rendering friend-circles-setup');
        console.log('🔵 User state:', user);
        console.log('🔵 isLoading:', isLoading);

        // CRITICAL FIX: Check localStorage directly for auth state
        // Don't rely solely on user context state during initial load
        const checkAuthState = () => {
          const cachedUser = localStorage.getItem('vibe_user_identity');
          const authSuccess = localStorage.getItem('vibe_auth_success');
          const userId = localStorage.getItem('vibe_user_id');

          console.log('🔍 Auth state check:');
          console.log('  - cachedUser:', cachedUser);
          console.log('  - authSuccess:', authSuccess);
          console.log('  - userId:', userId);
          console.log('  - user from context:', user);

          // If we have cached auth data, user is authenticated
          if ((cachedUser && authSuccess === 'true') || userId) {
            return true;
          }

          // Also check user context
          if (user) {
            return true;
          }

          return false;
        };

        const isAuthenticated = checkAuthState();
        console.log('🔐 Final auth decision:', isAuthenticated);

        if (!isAuthenticated) {
          console.log('⚠️ No authenticated user found, redirecting to signup');
          window.location.href = '/signup.html';
          return null;
        }

        // Helper to get current user ID
        const getCurrentUserId = () => {
          const identityStr = localStorage.getItem('vibe_user_identity');
          console.log('🔵 vibe_user_identity from localStorage:', identityStr);
          if (identityStr) {
            try {
              const identity = JSON.parse(identityStr);
              console.log('🔵 Parsed identity:', identity);
              return identity.userId;
            } catch (error) {
              console.warn('Failed to parse user identity:', error);
            }
          }
          const userId = user?.userId || 'guest';
          console.log('🔵 Final userId:', userId);
          return userId;
        };

        const currentUserId = getCurrentUserId();
        console.log('🔵 About to render CircleSetup with userId:', currentUserId);

        return (
          <CircleSetup
            userId={currentUserId}
            onComplete={() => setCurrentView('circle-dashboard')}
          />
        );

      case 'circle-dashboard':
        // Circle dashboard - requires authentication
        if (!user) {
          window.location.href = '/signup.html';
          return null;
        }

        const getUserId = () => {
          const identityStr = localStorage.getItem('vibe_user_identity');
          if (identityStr) {
            try {
              const identity = JSON.parse(identityStr);
              return identity.userId;
            } catch (error) {
              console.warn('Failed to parse user identity:', error);
            }
          }
          return user?.userId || 'guest';
        };

        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyCircleDashboard userId={getUserId()} />
          </React.Suspense>
        );

      case 'my-circles':
        // My Circles page - manage all user's circles
        if (!user) {
          window.location.href = '/signup.html';
          return null;
        }

        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyMyCirclesPage
              onBack={() => setCurrentView('circle-dashboard')}
              onCircleSelect={(circleId) => {
                console.log('Selected circle:', circleId);
                setCurrentView('circle-dashboard');
              }}
            />
          </React.Suspense>
        );

      // 'life-design' and 'life-design-landing' used to open a promo for
      // "Life Design" modules this app doesn't have, with completion badges.
      // Nothing linked to it; it went with the rest of the gamification
      // (October 2026), and an old link now lands on the home page.

      case 'data-privacy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeDataPrivacyPage />
          </React.Suspense>
        );
        
      // The footer has pointed here since the clone; the page existed but was
      // never routed, so the link fell through to the home page. Terms are
      // DDD-wide and link out to divedeepdialogues.com/terms instead.
      case 'privacy-policy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyPrivacyPolicyPage />
          </React.Suspense>
        );

      case 'resources':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeResourcesPage />
          </React.Suspense>
        );
        
      case 'life-design-hub':
        // Placeholder for new Conscious Life Design modules
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32">
            <div className="container mx-auto px-4 py-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-2xl mx-auto"
              >
                <div className="text-6xl mb-6">🔮</div>
                <h1 className="text-4xl font-bold text-white mb-4">
                  Conscious Life Design Hub
                </h1>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                  <p className="text-xl text-purple-200 mb-6">
                    Your gateway to intentional life creation and future self work.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h3 className="font-semibold text-white mb-2">Advanced Modules</h3>
                      <p className="text-sm text-slate-300">Life design, identity work, and future planning</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h3 className="font-semibold text-white mb-2">Interactive Tools</h3>
                      <p className="text-sm text-slate-300">Vision boards, goal mapping, and reflection exercises</p>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-yellow-400 text-black rounded-full inline-block font-semibold">
                    🚧 Coming Soon - Under Development 🚧
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        );
        
      
      
      case 'coaching':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyCoachingPage />
          </React.Suspense>
        );

      case 'coaching-privacy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyCoachingPrivacyPage />
          </React.Suspense>
        );

      case 'guide-selection':
        // Guide character selection - shown during onboarding or from settings
        return (
          <GuideSelection
            isOnboarding={true}
            onComplete={(guide) => {
              console.log('🎯 Guide selected:', guide?.name);
              // After selecting guide, go to dashboard or assessment
              setCurrentView('dashboard');
            }}
          />
        );

      case 'home':
      default:
        // ALWAYS show the friendship homepage - for both guests and authenticated users
        return <FriendshipVibeHomePage />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SkipLink href="#main-content" />
      <Navigation />

      {/* Temporarily disable all AnimatePresence to isolate the useEffect issue */}
      <main id="main-content" tabIndex="-1" className="flex-1">
        {renderCurrentView()}
      </main>

      {/* Global Footer */}
      <Footer />

      {/* ADD PWA INSTALL PROMPT HERE - Shows globally across all views */}
      <PWAInstallPrompt />

      {/* Guide Support - Floating chat button and Nana wisdom popups */}
      <FloatingGuideButton />
      <NanaWisdomPopup />
    </div>
  );
};

// Create a simple context for navigation
const NavigationContext = createContext();

// Hook to trigger signup from navigation
window.triggerSignup = () => {
  window.location.href = `${window.location.origin }?signup=true`;
  window.location.reload();
};

// Top-level router before any context
const TopLevelRouter = () => {
  const [currentView, setCurrentView] = useState('home');

  useEffect(() => {
    // Check URL parameters for initial view
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔍 TopLevelRouter: URL params:', window.location.search);
    console.log('🔍 TopLevelRouter: signup param:', urlParams.get('signup'));
    console.log('🔍 TopLevelRouter: view param:', urlParams.get('view'));

    // Check for signup mode
    if (urlParams.get('signup') === 'true') {
      console.log('🎯 TopLevelRouter: Setting view to signup');
      setCurrentView('signup');
    }
    // Check for view parameter (e.g., friend-circles-welcome)
    else if (urlParams.get('view')) {
      const viewParam = urlParams.get('view');
      console.log('🎯 TopLevelRouter: Setting view to:', viewParam);
      setCurrentView(viewParam);
    }
  }, []);
  
  // Handle signup completely outside of any context
  if (currentView === 'signup') {
    console.log('🚀 TopLevelRouter: Rendering StandaloneSignup - NO CONTEXTS ACTIVE');
    return (
      <StandaloneSignup 
        onComplete={() => {
          // Navigate to dashboard after signup
          window.location.href = window.location.origin;
        }}
        onSkip={() => {
          // Navigate to home as guest
          window.location.href = window.location.origin;
        }}
        onBack={() => {
          // Navigate back to home
          window.location.href = window.location.origin;
        }}
      />
    );
  }
  
  // For all other views, use the full app with contexts
  return (
    <NavigationContext.Provider value={{ currentView, setCurrentView }}>
      <ErrorBoundary name="App">
        <PrivacyAuthProvider>
          <AppProvider>
            <AppRoutingWrapper />
          </AppProvider>
        </PrivacyAuthProvider>
      </ErrorBoundary>
    </NavigationContext.Provider>
  );
};

// Main App component with all providers
const App = () => {
  // Initialise error tracking and the progress record
  useEffect(() => {
    // Set up error tracking context
    errorTrackingService.setContext({
      appVersion: '2.0.0',
      edition: 'vibe',
      environment: import.meta.env.MODE,
    });

    // Which modules you've finished and the insights you've saved: fold in
    // the copy in this app's database if you're signed in (and clean any old
    // XP, level or streak fields out of it). Local-first: a failed read
    // changes nothing on this device.
    import('./stores/progressStore').then(({ initialiseProgress }) => {
      initialiseProgress();
    }).catch(() => { /* this browser's copy still works */ });

    // Check if user logged in via HTML pages
    const authSuccess = localStorage.getItem('vibe_auth_success');
    const userId = localStorage.getItem('vibe_user_id');

    if (authSuccess === 'true' && userId) {
      console.log('User authenticated via HTML signup:', userId);
      // Clear the flags
      localStorage.removeItem('vibe_auth_success');

      // The PrivacyAuthContext should pick up the Firebase auth state
      // and update accordingly
    }
  }, []);

  return <TopLevelRouter />;
};

// Separate routing wrapper to handle signup isolation
const AppRoutingWrapper = () => {
  const { currentView, setCurrentView } = useContext(NavigationContext);
  // Override the app context currentView with our prop
  const appContext = useApp();
  useEffect(() => {
    appContext.setCurrentView(currentView);
  }, [currentView, appContext.setCurrentView]);
  
  
  // Handle login form isolated as well
  if (currentView === 'login') {
    return (
      <div className="min-h-screen">
        <Navigation />
        <VibeLogin 
          onComplete={() => setCurrentView('dashboard')} 
          onBackToSignup={() => setCurrentView('signup')} 
        />
        <PWAInstallPrompt />
      </div>
    );
  }
  
  // All other views get the full context
  return (
    <ErrorBoundary name="AppContent">
      <GuideProvider>
        <AppContent />
      </GuideProvider>
    </ErrorBoundary>
  );
};

export default App;